import { Checkbox, Radio, RadioChangeEvent, TableColumnsType } from 'antd'
import { CheckboxChangeEvent } from 'antd/es/checkbox'
import React, { useMemo } from 'react'

import { FunctionPermission } from '@/model/function-permission'
import { Menu } from '@/model/menu'
import { DataPermission, Role } from '@/model/role'

import { DataType, MenuFunctionPermission } from '../role-to-table-data'

function checkDataPermissions(arr: (DataPermission | undefined)[]) {
  if (arr.length === 0) return null
  const firstElement = arr[0]
  const allSame = arr.every((element) => element === firstElement)
  return allSame ? firstElement : null
}

export default function useSetPermissionsColumns({
  role,
  setRole,
  selectedRowKeys,
  functionPermissions,
}: {
  role: Role;
  setRole: React.Dispatch<React.SetStateAction<Role>>;
  selectedRowKeys: number[];
  functionPermissions: FunctionPermission[];
}) {
  const columns: TableColumnsType<DataType> = useMemo(() => {
    const onDataPermissionChange = (
      evt: RadioChangeEvent,
      record: DataType,
    ) => {
      const val = evt.target.value as DataPermission
      setRole((role) => {
        return {
          ...role,
          menuPermissions: role.menuPermissions.map((m) => {
            return m.menuId === record.key ? { ...m, dataPermission: val } : m
          }),
        }
      })
    }

    const onFunctionPermissionChange = (evt: CheckboxChangeEvent) => {
      const { value, checked } = evt.target as {
        value: string;
        checked: boolean;
      }
      const functionId = parseInt(value)

      setRole((role) => {
        return {
          ...role,
          functionIds: checked
            ? [...role.functionIds, functionId]
            : role.functionIds.filter((id) => id !== functionId),
        }
      })
    }

    const rowSelected = (record: DataType) => {
      return selectedRowKeys.includes(record.key)
    }

    const onAllFunctionPermissionChange = (evt: CheckboxChangeEvent) => {
      const checked = evt.target.checked
      const functionIds = checked ? functionPermissions.map((fp) => fp.id) : []

      setRole((role) => {
        return {
          ...role,
          functionIds,
        }
      })
    }

    const onAllDataPermissionChange = (evt: RadioChangeEvent) => {
      const val = evt.target.value as DataPermission
      setRole((role) => {
        return {
          ...role,
          menuPermissions: role.menuPermissions.map((m) => {
            return { ...m, dataPermission: val }
          }),
        }
      })
    }

    function renderFunctionPermissions(
      permissions: MenuFunctionPermission[],
      record: DataType,
    ) {
      if (record.key === 0) {
        return (
          <Checkbox
            checked={role.functionIds.length === functionPermissions.length}
            onChange={onAllFunctionPermissionChange}
          >
            All
          </Checkbox>
        )
      }

      // 0 is the all menu
      if (record.children?.length) return '-'
      const selected = rowSelected(record)

      return permissions.map((p) => {
        const checked = !selected ? false : p.checked || p.forceEnabled
        const disabled = !selected || p.forceEnabled

        return (
          <Checkbox
            key={p.id}
            disabled={disabled}
            checked={checked}
            value={p.id}
            onChange={onFunctionPermissionChange}
          >
            {p.name}
          </Checkbox>
        )
      })
    }

    function renderDataPermissions(
      dataPermission: DataPermission | undefined,
      record: DataType,
    ) {
      // 0 is the all menu
      if (record.key === 0) {
        return (
          <Radio.Group
            value={checkDataPermissions(
              role.menuPermissions.map((m) => m.dataPermission),
            )}
            onChange={onAllDataPermissionChange}
          >
            <Radio value={DataPermission.All}>All data</Radio>
            <Radio value={DataPermission.SelfAndSubordinates}>
              Self and subordinates
            </Radio>
            <Radio value={DataPermission.Self}>Data about self</Radio>
          </Radio.Group>
        )
      }

      if (record.children?.length) return '-'

      const selected = rowSelected(record)
      const value = !selected
        ? undefined
        : dataPermission || DataPermission.Self

      return (
        <Radio.Group
          value={value}
          disabled={!selected}
          onChange={(evt) => onDataPermissionChange(evt, record)}
        >
          <Radio value={DataPermission.All}>All data</Radio>
          <Radio value={DataPermission.SelfAndSubordinates}>
            Self and subordinates
          </Radio>
          <Radio value={DataPermission.Self}>Data about self</Radio>
        </Radio.Group>
      )
    }

    return [
      {
        title: 'Menus',
        dataIndex: 'menu',
        key: 'menu',
        width: 300,
        render(menu: Menu) {
          return menu.name
        },
      },
      {
        title: 'Function permissions',
        dataIndex: 'functionPermissions',
        key: 'functionPermissions',
        render: renderFunctionPermissions,
      },
      {
        title: 'Data permissions',
        dataIndex: 'dataPermission',
        width: '40%',
        key: 'dataPermission',
        render: renderDataPermissions,
      },
    ]
  }, [selectedRowKeys, functionPermissions, role, setRole])

  return columns
}
